import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Paper, Button, Chip, LinearProgress, Collapse,
  alpha, CircularProgress,
} from '@mui/material';
import {
  CheckCircle, RadioButtonUnchecked, ArrowForward, CloudUpload, Lock,
  ExpandMore, ExpandLess, AutoAwesome, Celebration,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { buildStages, countDocumentsByType } from './guidedSteps';
import { DASH, flowColor } from './dashboardColors';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
// The API runs as a Vercel serverless function, which rejects request bodies over 4.5 MB.
const MAX_FILE_BYTES = 4.5 * 1024 * 1024;
const ACCEPT = 'application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png';
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
const SUCCESS = DASH.success;
const NAVY = DASH.ink;

const ActionButton = ({ label, color, highlight, loading, disabled, onClick, icon }) => (
  <Button
    size="small"
    variant={highlight ? 'contained' : 'outlined'}
    endIcon={loading ? <CircularProgress size={14} color="inherit" /> : (icon || <ArrowForward sx={{ fontSize: 14 }} />)}
    disabled={disabled}
    onClick={onClick}
    sx={{
      whiteSpace: 'nowrap',
      fontWeight: 600,
      fontSize: '0.75rem',
      borderRadius: 1.5,
      ...(highlight
        ? { bgcolor: color, '&:hover': { bgcolor: alpha(color, 0.9) } }
        : { borderColor: color, color, '&:hover': { borderColor: color, bgcolor: alpha(color, 0.08) } }),
    }}
  >
    {label}
  </Button>
);

const TaskRow = ({ done, locked, label, description, progressLabel, children }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      gap: 1.5,
      py: 1.25,
      px: 1.5,
      borderRadius: 2,
      bgcolor: done ? alpha(SUCCESS, 0.04) : 'background.paper',
      border: '1px solid',
      borderColor: done ? alpha(SUCCESS, 0.25) : 'grey.200',
    }}
  >
    <Box sx={{ pt: 0.25, flexShrink: 0 }}>
      {done ? (
        <CheckCircle sx={{ color: SUCCESS, fontSize: 20 }} />
      ) : locked ? (
        <Lock sx={{ color: 'grey.400', fontSize: 20 }} />
      ) : (
        <RadioButtonUnchecked sx={{ color: 'grey.400', fontSize: 20 }} />
      )}
    </Box>
    <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: done ? 'text.secondary' : NAVY,
            textDecoration: done ? 'line-through' : 'none',
          }}
        >
          {label}
        </Typography>
        {progressLabel && (
          <Typography variant="caption" sx={{ color: done ? SUCCESS : 'text.secondary', fontWeight: 600 }}>
            {progressLabel}
          </Typography>
        )}
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.4, display: 'block' }}>
        {description}
      </Typography>
    </Box>
    {children && (
      <Box
        sx={{
          display: 'flex', gap: 1, flexWrap: 'wrap', flex: '0 1 auto', minWidth: 0,
          ml: { xs: 4, sm: 0 }, maxWidth: { xs: 'calc(100% - 32px)', sm: '100%' },
        }}
      >
        {children}
      </Box>
    )}
  </Box>
);

const ActionChecklist = ({
  type = 'fastFunding',
  hasApplication = false,
  hasPFS = false,
  documents = [],
  onBankStatementsClick,
  onDocumentsChanged,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loadingAction, setLoadingAction] = useState(null);
  const [uploadingType, setUploadingType] = useState(null);
  const [hasDebtSchedule, setHasDebtSchedule] = useState(false);
  const [expanded, setExpanded] = useState({});
  const fileInputRef = useRef(null);
  const pendingTypeRef = useRef(null);
  const prevActiveRef = useRef(null);
  const primaryColor = flowColor(type);

  const docList = Array.isArray(documents) ? documents : [];
  const stages = useMemo(() => {
    const counts = countDocumentsByType(docList);
    return buildStages(t, hasApplication, (docType) => counts[docType] || 0);
  }, [docList, hasApplication, t]);

  const activeIndex = stages.findIndex((s) => !s.done);
  const allDone = activeIndex === -1;
  const activeKey = allDone ? null : stages[activeIndex].key;
  const allReqs = stages.flatMap((s) => s.requirements);
  const doneReqs = allReqs.filter((r) => r.done).length;
  const progress = (doneReqs / allReqs.length) * 100;

  useEffect(() => {
    const fetchDebtSchedule = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_URL}/api/debt-schedule`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const schedules = res.data?.data || [];
        setHasDebtSchedule(schedules.length > 0 && schedules[0].debts?.length > 0);
      } catch (err) {
        console.error('Error fetching debt schedule status:', err);
      }
    };
    fetchDebtSchedule();
  }, []);

  // Keep the current stage open, and celebrate when the user moves to the next one.
  useEffect(() => {
    const prev = prevActiveRef.current;
    prevActiveRef.current = activeIndex;
    setExpanded(activeKey ? { [activeKey]: true } : {});
    if (prev === null || prev === activeIndex) return;
    const advanced = activeIndex === -1 || (prev !== -1 && activeIndex > prev);
    if (!advanced) return;
    if (activeIndex === -1) {
      toast.success(t('dashboard.checklist.allCompleteToast'));
    } else {
      toast.success(t('dashboard.checklist.stageCompleteToast', {
        stage: stages[prev].title,
        next: stages[activeIndex].title,
      }));
    }
  }, [activeIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExternalRedirect = async (appType) => {
    setLoadingAction(appType);
    try {
      const token = localStorage.getItem('token');
      const baseUrl = appType === 'pfs'
        ? (import.meta.env.VITE_PFS_URL || 'https://pfs.corebusinesscapital.com')
        : (import.meta.env.VITE_FINANCING_APP_URL || 'https://app.corebusinesscapital.com');

      const url = new URL(baseUrl);
      const returnParam = appType === 'pfs' ? 'pfs_submitted=true' : 'submitted=true';
      const returnUrl = `${import.meta.env.VITE_CORELOANHUB_URL || 'http://localhost:3000'}/dashboard?${returnParam}&source=${appType === 'pfs' ? 'pfs-copilot' : 'financing-app'}`;
      url.searchParams.append('returnUrl', returnUrl);

      if (user?.email) {
        url.searchParams.append('email', user.email);
      }

      const response = await axios.post(
        `${API_URL}/api/auth/generate-single-use-token`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        url.searchParams.append('authToken', response.data.token);
      }

      window.location.href = url.toString();
    } catch (error) {
      console.error('Failed to generate redirect:', error);
      setLoadingAction(null);
    }
  };

  const openFilePicker = (docType, multiple) => {
    const input = fileInputRef.current;
    if (!input) return;
    pendingTypeRef.current = docType;
    input.value = '';
    input.multiple = multiple;
    input.click();
  };

  const handleFilesSelected = async (event) => {
    const docType = pendingTypeRef.current;
    const files = Array.from(event.target.files || []);
    if (!docType || !files.length) return;

    const valid = files.filter((file) => {
      if (!ALLOWED_TYPES.includes(file.type) && !/\.(pdf|jpe?g|png)$/i.test(file.name)) {
        toast.error(`${file.name}: ${t('bankUpload.badType', 'Only PDF, JPG or PNG files are accepted.')}`);
        return false;
      }
      if (file.size > MAX_FILE_BYTES) {
        toast.error(`${file.name}: ${t('bankUpload.tooLarge', 'This file is over 4.5 MB. Please save a smaller PDF or email it to your advisor.')}`);
        return false;
      }
      return true;
    });
    if (!valid.length) return;

    setUploadingType(docType);
    const token = localStorage.getItem('token');
    let ok = 0;
    for (const file of valid) {
      try {
        const formData = new FormData();
        formData.append('document', file);
        formData.append('documentType', docType);
        formData.append('description', docType);
        await axios.post(`${API_URL}/api/documents/upload`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        ok++;
      } catch (err) {
        const status = err.response?.status;
        let message = err.response?.data?.message || t('docs.failedToUpload', 'Failed to upload document');
        if (status === 413) message = t('bankUpload.tooLarge', 'This file is over 4.5 MB. Please save a smaller PDF or email it to your advisor.');
        if (status === 429) message = t('bankUpload.rateLimited', 'Too many uploads at once. Please wait a few minutes and try again.');
        toast.error(`${file.name}: ${message}`);
      }
    }
    if (ok > 0) {
      toast.success(t('dashboard.checklist.uploadedCount', { count: ok }));
      await onDocumentsChanged?.();
    }
    setUploadingType(null);
  };

  const busy = !!loadingAction || !!uploadingType;

  const renderRequirementActions = (req) => {
    if (req.done) return null;
    if (req.kind === 'application') {
      return (
        <ActionButton
          label={t('dashboard.checklist.startApplication')}
          color={primaryColor}
          highlight
          loading={loadingAction === 'financing'}
          disabled={busy}
          onClick={() => handleExternalRedirect('financing')}
        />
      );
    }
    if (req.kind === 'bank') {
      return (
        <ActionButton
          label={t('dashboard.checklist.uploadNow')}
          color={primaryColor}
          highlight
          icon={<CloudUpload sx={{ fontSize: 16 }} />}
          disabled={busy}
          onClick={() => (onBankStatementsClick ? onBankStatementsClick() : navigate('/documents'))}
        />
      );
    }
    return req.options.map((opt, i) => (
      <ActionButton
        key={opt.docType}
        label={opt.label || t('dashboard.checklist.uploadNow')}
        color={primaryColor}
        highlight={i === 0}
        icon={<CloudUpload sx={{ fontSize: 16 }} />}
        loading={uploadingType === opt.docType}
        disabled={busy}
        onClick={() => openFilePicker(opt.docType, req.required - req.count > 1)}
      />
    ));
  };

  const statusChip = (status) => {
    const config = {
      done: { label: t('dashboard.checklist.statusComplete'), color: SUCCESS },
      active: { label: t('dashboard.checklist.statusCurrent'), color: primaryColor },
      locked: { label: t('dashboard.checklist.statusLocked'), color: DASH.muted },
    }[status];
    return (
      <Chip
        size="small"
        label={config.label}
        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: alpha(config.color, 0.12), color: config.color }}
      />
    );
  };

  return (
    <Paper
      id="next-steps"
      elevation={0}
      sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'grey.200', bgcolor: 'background.paper', scrollMarginTop: 80 }}
    >
      <input type="file" hidden ref={fileInputRef} accept={ACCEPT} onChange={handleFilesSelected} />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: NAVY }}>
          {t('dashboard.checklist.title')}
        </Typography>
        <Chip
          size="small"
          label={allDone
            ? t('dashboard.checklist.allStagesComplete')
            : t('dashboard.checklist.stageOf', { current: activeIndex + 1, total: stages.length })}
          sx={{ bgcolor: alpha(allDone ? SUCCESS : primaryColor, 0.1), color: allDone ? SUCCESS : primaryColor, fontWeight: 700 }}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: 'grey.100',
            '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: allDone ? SUCCESS : primaryColor },
          }}
        />
        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.75, display: 'block' }}>
          {t('dashboard.checklist.requirementsDone', { done: doneReqs, total: allReqs.length })}
        </Typography>
      </Box>

      {allDone && (
        <Box
          sx={{
            display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 2, mb: 2, borderRadius: 2,
            bgcolor: alpha(SUCCESS, 0.08), border: '1px solid', borderColor: alpha(SUCCESS, 0.3),
          }}
        >
          <Celebration sx={{ color: SUCCESS }} />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: NAVY }}>
              {t('dashboard.checklist.allCompleteTitle')}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('dashboard.checklist.allCompleteDesc')}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Guided stages */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {stages.map((stage, index) => {
          const status = stage.done ? 'done' : index === activeIndex ? 'active' : 'locked';
          const isOpen = !!expanded[stage.key];
          const stageDone = stage.requirements.filter((r) => r.done).length;
          const previousStage = stages[index - 1];

          return (
            <Box
              key={stage.key}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: status === 'active'
                  ? alpha(primaryColor, 0.5)
                  : status === 'done' ? alpha(SUCCESS, 0.3) : 'grey.200',
                bgcolor: status === 'active'
                  ? alpha(primaryColor, 0.03)
                  : status === 'done' ? alpha(SUCCESS, 0.03) : 'transparent',
                boxShadow: status === 'active' ? `0 2px 10px ${alpha(primaryColor, 0.12)}` : 'none',
                transition: 'all 0.2s ease',
              }}
            >
              {/* Stage header */}
              <Box
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
                onClick={() => setExpanded((prev) => ({ ...prev, [stage.key]: !prev[stage.key] }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExpanded((prev) => ({ ...prev, [stage.key]: !prev[stage.key] }));
                  }
                }}
                sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, cursor: 'pointer' }}
              >
                <Box
                  sx={{
                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: '0.85rem',
                    ...(status === 'done' && { bgcolor: alpha(SUCCESS, 0.12), color: SUCCESS }),
                    ...(status === 'active' && { bgcolor: primaryColor, color: '#fff' }),
                    ...(status === 'locked' && { bgcolor: 'grey.100', color: 'grey.500' }),
                  }}
                >
                  {status === 'done' ? <CheckCircle sx={{ fontSize: 22 }} />
                    : status === 'locked' ? <Lock sx={{ fontSize: 16 }} />
                      : index + 1}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: status === 'locked' ? 'text.secondary' : NAVY }}
                    >
                      {stage.title}
                    </Typography>
                    {statusChip(status)}
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>
                    {stage.subtitle}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, flexShrink: 0 }}>
                  {stageDone}/{stage.requirements.length}
                </Typography>
                {isOpen ? <ExpandLess sx={{ color: 'grey.500' }} /> : <ExpandMore sx={{ color: 'grey.500' }} />}
              </Box>

              {/* Stage details */}
              <Collapse in={isOpen} unmountOnExit>
                <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {status === 'active' && stage.intro && (
                    <Box
                      sx={{
                        display: 'flex', gap: 1, alignItems: 'flex-start', p: 1.5, borderRadius: 2,
                        bgcolor: DASH.cream, border: '1px solid', borderColor: alpha(DASH.sand, 0.6),
                      }}
                    >
                      <AutoAwesome sx={{ color: primaryColor, fontSize: 18, mt: 0.25 }} />
                      <Typography variant="body2" sx={{ color: NAVY, fontWeight: 500 }}>
                        {stage.intro}
                      </Typography>
                    </Box>
                  )}
                  {status === 'locked' && previousStage && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Lock sx={{ fontSize: 14 }} />
                      {t('dashboard.checklist.unlockAfter', { stage: previousStage.title })}
                    </Typography>
                  )}
                  {stage.requirements.map((req) => (
                    <TaskRow
                      key={req.key}
                      done={req.done}
                      locked={status === 'locked'}
                      label={req.label}
                      description={req.description}
                      progressLabel={req.kind !== 'application' && req.required > 1
                        ? t('dashboard.checklist.uploadedOf', { count: Math.min(req.count, req.required), required: req.required })
                        : null}
                    >
                      {status !== 'locked' && renderRequirementActions(req)}
                    </TaskRow>
                  ))}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>

      {/* Optional boosters that aren't part of the document path */}
      <Typography
        variant="overline"
        sx={{ display: 'block', mt: 3, mb: 1, color: 'text.secondary', fontWeight: 700, letterSpacing: 0.8 }}
      >
        {t('dashboard.checklist.strengthenTitle')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TaskRow
          done={hasPFS}
          label={t('dashboard.checklist.completePFS')}
          description={t('dashboard.checklist.completePFSDesc')}
        >
          {!hasPFS && (
            <ActionButton
              label={t('dashboard.checklist.startPFS')}
              color={DASH.brown}
              highlight={type === 'sba'}
              loading={loadingAction === 'pfs'}
              disabled={busy}
              onClick={() => handleExternalRedirect('pfs')}
            />
          )}
        </TaskRow>
        <TaskRow
          done={hasDebtSchedule}
          label={t('dashboard.checklist.debtScheduleBuilder')}
          description={t('dashboard.checklist.debtScheduleBuilderDesc')}
        >
          {!hasDebtSchedule && (
            <ActionButton
              label={t('dashboard.checklist.startNow')}
              color={DASH.brown}
              disabled={busy}
              onClick={() => navigate('/debt-schedule')}
            />
          )}
        </TaskRow>
      </Box>
    </Paper>
  );
};

export default ActionChecklist;
